// Typed wrapper over the extracted style pattern (SPEC §9). Groove shows each
// style's real 1-bar pattern (DECISIONS: display only; v0.5 adds playback).

import lanesData from './generated/lanes.json';
import patData from './generated/pat.json';
import type { Cell, Kit, LaneId, PercKind, Role, StyleId } from './types';

/** The reference's synth-voice parameters for a style's fallback kit (SPEC F5 "Fallback voices"). */
export interface SynthKitParams {
  click?: number;
  kdec?: number;
  drive?: number;
  edec?: number;
}

interface RawPattern {
  bpm: number;
  swing: number;
  l: Partial<Record<LaneId, string>>;
  n: string;
  human?: boolean;
  push?: boolean;
  dusty?: boolean;
  kit?: SynthKitParams;
}

const PAT = patData as Record<StyleId, RawPattern>;
const LANE_ENTRIES = lanesData as [LaneId, string][];
const LANE_NAMES = new Map(LANE_ENTRIES);

/** Lane order as the reference itself declares it: K, 8, S, C, R, H, O, P, B. */
export const LANE_ORDER: LaneId[] = LANE_ENTRIES.map(([id]) => id);

/** SPEC F5 AC: lanes map to kit pads by role. R/P/B all share the perc role; `defaultLaneToPad` narrows them (v0.5.1). */
export const LANE_ROLE: Record<LaneId, Role> = {
  K: 'kick',
  '8': '808',
  S: 'snare',
  C: 'clap',
  R: 'perc',
  H: 'hat',
  O: 'hat_open',
  P: 'perc',
  B: 'perc',
};

export interface GrooveLane {
  lane: LaneId;
  name: string;
  cells: Cell[];
}

export interface GroovePattern {
  bpm: number;
  swing: number;
  note: string;
  lanes: GrooveLane[];
}

/** A style's full pattern data (SPEC F5), every lane present, ready for the groove engine. */
export interface StylePattern {
  styleId: StyleId;
  bpm: number;
  swing: number;
  bars: 1 | 2;
  humanize: boolean;
  dusty: boolean;
  /** Whether the Dilla kick/snare/clap push applies when humanize is on (SPEC F5, DECISIONS: reference-only styles, currently just dilla). */
  push: boolean;
  kit: SynthKitParams;
  lanes: Record<LaneId, Cell[]>;
  laneToPad: Partial<Record<LaneId, number>>;
  note: string;
}

/** The reference's dusty low-pass cutoff in Hz (SPEC F5); a groove can move it (SPEC 0.3.14). */
export const DUSTY_HZ = 8500;

const EMPTY_BAR = '................';
const toCells = (s: string) => s.split('') as Cell[];

/** The style's real 1-bar pattern, in the reference's own lane order. Only lanes the style actually uses. */
export function patternFor(styleId: StyleId): GroovePattern {
  const raw = PAT[styleId];
  const lanes = (Object.entries(raw.l) as [LaneId, string][]).map(([lane, cells]) => ({
    lane,
    name: LANE_NAMES.get(lane) ?? lane,
    cells: toCells(cells),
  }));
  return { bpm: raw.bpm, swing: raw.swing, note: raw.n, lanes };
}

/** The cycle a cell in this lane steps through on tap (SPEC F5 AC): every lane cycles off/hit/accent/ghost; the H lane also cycles 32nd and triplet rolls (reference `HCYC`, DECISIONS: H only, not O). */
export function nextCell(lane: LaneId, cell: Cell): Cell {
  const cycle = lane === 'H' ? '.xXgrt' : '.xXg';
  const idx = cycle.indexOf(cell);
  const next = cycle[(idx + 1) % cycle.length];
  return (next ?? '.') as Cell;
}

/** A lane's display name from the reference ("Kick", "Open"). */
export const laneName = (lane: LaneId): string => LANE_NAMES.get(lane) ?? lane;

/**
 * The default pad for a lane: the kit's first pad with a matching role, else a fixed fallback (DECISIONS 9).
 * The three perc lanes (SPEC F5 v0.5.1): R (rim) → the first pad holding a rim sample, else A07; P (shaker) →
 * the first perc pad that prefers shaker; B (cowbell) → the first perc pad that prefers cowbell. A lane with no
 * such pad takes the free perc pads in pad order, one pad each, so rim, shaker and cowbell never share a pad;
 * with no perc pad left it falls back to its fixed pad, or the next free one (DECISIONS 2026-10-06).
 */
export const DEFAULT_LANE_PAD: Record<LaneId, number> = { K: 0, S: 1, H: 2, O: 3, C: 5, R: 6, P: 7, B: 8, '8': 15 };

/** The kind P and B look for first (SPEC F5 v0.5.1). */
const LANE_KIND: Partial<Record<LaneId, PercKind>> = { P: 'shaker', B: 'cowbell' };

/** `rims`: the IDs of rim samples, so R finds the pad holding one. */
export function defaultLaneToPad(kit?: Kit, rims?: ReadonlySet<string>): Partial<Record<LaneId, number>> {
  const out: Partial<Record<LaneId, number>> = {};
  const pads = kit?.pads ?? [];
  const taken = new Set<number>();
  const percLanes = LANE_ORDER.filter((lane) => LANE_ROLE[lane] === 'perc');
  for (const lane of LANE_ORDER) {
    const role = LANE_ROLE[lane];
    if (role === 'perc') continue;
    const matched = pads.findIndex((p) => p.role === role);
    out[lane] = matched >= 0 ? matched : DEFAULT_LANE_PAD[lane];
  }
  // First pass: the pad each perc lane is for (a rim sample, a preferred kind), so a lane without one can't take it.
  for (const lane of percLanes) {
    const kind = LANE_KIND[lane];
    const pad = lane === 'R'
      ? pads.findIndex((p, i) => !taken.has(i) && !!p.sampleId && !!rims?.has(p.sampleId))
      : pads.findIndex((p, i) => !taken.has(i) && p.role === 'perc' && !!kind && p.percKind === kind);
    if (pad >= 0) {
      out[lane] = pad;
      taken.add(pad);
    } else if (lane === 'R' && !taken.has(DEFAULT_LANE_PAD.R)) {
      out[lane] = DEFAULT_LANE_PAD.R;
      taken.add(DEFAULT_LANE_PAD.R);
    }
  }
  // Second pass: the rest take the free perc pads in pad order, else their fixed pad or the next free one.
  for (const lane of percLanes) {
    if (out[lane] !== undefined) continue;
    let pad = pads.findIndex((p, i) => p.role === 'perc' && !taken.has(i));
    if (pad < 0) {
      pad = DEFAULT_LANE_PAD[lane];
      while (taken.has(pad)) pad = (pad + 1) % 16;
    }
    taken.add(pad);
    out[lane] = pad;
  }
  return out;
}

/** All 9 lanes, filled to 16 steps each (empty lanes are all-off), for the groove engine (SPEC F5). */
export function stylePattern(styleId: StyleId, kit?: Kit): StylePattern {
  const raw = PAT[styleId];
  const lanes = {} as Record<LaneId, Cell[]>;
  for (const lane of LANE_ORDER) lanes[lane] = toCells(raw.l[lane] ?? EMPTY_BAR);
  return {
    styleId,
    bpm: raw.bpm,
    swing: raw.swing,
    bars: 1,
    humanize: !!raw.human,
    dusty: !!raw.dusty,
    push: !!raw.push,
    kit: raw.kit ?? {},
    lanes,
    laneToPad: defaultLaneToPad(kit),
    note: raw.n,
  };
}

/** Switches between 1 and 2 bars (SPEC F5 AC): growing to 2 bars copies bar 1 into bar 2; shrinking back to 1 keeps bar 1. */
export function setBars(pattern: StylePattern, bars: 1 | 2): StylePattern {
  if (bars === pattern.bars) return pattern;
  const perBar = 16;
  const lanes = {} as Record<LaneId, Cell[]>;
  for (const lane of LANE_ORDER) {
    const bar1 = pattern.lanes[lane].slice(0, perBar);
    lanes[lane] = bars === 2 ? [...bar1, ...bar1] : bar1;
  }
  return { ...pattern, bars, lanes };
}
